import type { CartItem, InventoryAllocation, PosCartData, Product } from '../../services/pos.service'

export interface ControlledInventorySelection {
  product: Product
  quantity: number
  mode: 'create' | 'update'
  itemId?: number
  currentQuantity: number
  version?: number
  searchKey?: string
}

interface ControlledInventoryResult {
  quantity: number
  allocations: InventoryAllocation[]
}

interface ControlledInventoryCartGateway {
  createCartItem: (payload: { product_id: number; quantity: number; allocations?: InventoryAllocation[] }) => Promise<PosCartData>
  updateCartItem: (itemId: number, payload: { quantity: number; expected_version: number; allocations?: InventoryAllocation[] }) => Promise<PosCartData>
  deleteCartItem: (itemId: number) => Promise<PosCartData>
}

export function coordinateControlledInventory(
  product: Product,
  cart: PosCartData | undefined,
  increment = 1,
  searchKey?: string,
): ControlledInventorySelection {
  const currentItem = cart?.items?.find(
    (item: CartItem) => Number(item.product_id ?? item.id) === product.id,
  )

  if (!currentItem) {
    return {
      product,
      quantity: Math.max(1, increment),
      currentQuantity: 0,
      mode: 'create',
      searchKey,
    }
  }

  return {
    product,
    quantity: Math.max(1, currentItem.quantity + increment),
    currentQuantity: currentItem.quantity,
    version: currentItem.version,
    mode: 'update',
    itemId: product.id,
    searchKey,
  }
}

export function persistControlledInventorySelection(
  selection: ControlledInventorySelection,
  result: ControlledInventoryResult,
  cartGateway: ControlledInventoryCartGateway,
): Promise<PosCartData> {
  if (selection.mode === 'update' && result.quantity === 0) {
    return cartGateway.deleteCartItem(selection.itemId!)
  }

  if (result.quantity <= 0) {
    return Promise.reject(new Error('Un producto nuevo debe tener al menos una unidad.'))
  }

  if (selection.mode === 'create') {
    return cartGateway.createCartItem({
      product_id: selection.product.id,
      quantity: result.quantity,
      allocations: result.allocations,
    })
  }

  return cartGateway.updateCartItem(selection.itemId!, {
    quantity: result.quantity,
    expected_version: selection.version!,
    allocations: result.allocations,
  })
}
